import composerStyles from '@ui/Composer/Composer.module.scss'
import styles from './SessionWalkthrough.module.scss'

// --- Types ---

type SliderMockContent = {
    type: 'slider'
    leftEmoji: string
    rightEmoji: string
    value: number // 0-1, frozen position
}

type ToggleAndWritingMockContent = {
    type: 'toggleAndWriting'
    optionA: string
    optionB: string
    selected: 'a' | 'b'
    writingPrompt: string
    writingSample: string
}

type DeliveryMockContent = {
    type: 'delivery'
    acknowledgement: string
}

type FrameContent = SliderMockContent | ToggleAndWritingMockContent | DeliveryMockContent

type WalkthroughFrame = {
    title: string
    description: string
    content: FrameContent
}

export type SessionWalkthroughProps = {
    /** Without it the walkthrough carries no heading of its own (the feature page titles its section). */
    sectionTitle?: string
    frames: [WalkthroughFrame, WalkthroughFrame, WalkthroughFrame]
    shuffleHint?: string
    /** Stands inside a page section: no outer padding, each frame's words over its screen. */
    bare?: boolean
}

// --- Inlined SVG Icons (same as Composer inputs) ---

const ShuffleIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 18h1.4c1.3 0 2.5-.6 3.3-1.7l6.1-8.6c.7-1.1 2-1.7 3.3-1.7H20" />
        <path d="m18 2 4 4-4 4" />
        <path d="M2 6h1.9c1.5 0 2.9.9 3.6 2.2" />
        <path d="M20 18h-5.9c-1.3 0-2.6-.7-3.3-1.8l-.5-.8" />
        <path d="m18 14 4 4-4 4" />
    </svg>
)

const ContinueIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14" />
        <path d="m12 5 7 7-7 7" />
    </svg>
)

const PlayIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
        <path d="M8 5v14l11-7z" />
    </svg>
)

// --- Mock Action Buttons (inert divs mimicking Composer's action row) ---

const MockActionRow = ({showShuffleHint, shuffleHint}: {showShuffleHint?: boolean, shuffleHint?: string}) => (
    <div className={styles.shuffleHintWrapper}>
        <div className={composerStyles.actionButtonRow}>
            <div className={composerStyles.actionButton}>
                <div className={composerStyles.actionButtonInner}>
                    <ShuffleIcon />
                </div>
            </div>
            <div className={composerStyles.actionButton}>
                <div className={composerStyles.actionButtonInner}>
                    <ContinueIcon />
                </div>
            </div>
        </div>
        {showShuffleHint && shuffleHint && (
            <span className={styles.shuffleHint}>{shuffleHint}</span>
        )}
    </div>
)

// --- Mock Panels ---

const SliderMock = ({content, shuffleHint}: {content: SliderMockContent, shuffleHint?: string}) => (
    <div className={styles.mockPanel} aria-hidden="true">
        <div className={composerStyles.sliderContainer}>
            <span className={composerStyles.sliderEmoji}>{content.leftEmoji}</span>
            <input
                type="range"
                min={0}
                max={1}
                step={0.25}
                value={content.value}
                disabled
                className={composerStyles.slider}
                tabIndex={-1}
            />
            <span className={composerStyles.sliderEmoji}>{content.rightEmoji}</span>
        </div>
        <MockActionRow showShuffleHint shuffleHint={shuffleHint} />
    </div>
)

const ToggleAndWritingMock = ({content}: {content: ToggleAndWritingMockContent}) => (
    <div className={styles.mockPanel} aria-hidden="true">
        <div className={composerStyles.toggleContainer}>
            <div className={`${composerStyles.toggleOption} ${content.selected === 'a' ? composerStyles.selected : ''}`}>
                {content.optionA}
            </div>
            <span className={composerStyles.toggleDivider}>or</span>
            <div className={`${composerStyles.toggleOption} ${content.selected === 'b' ? composerStyles.selected : ''}`}>
                {content.optionB}
            </div>
        </div>
        {content.writingPrompt && (
            <p className={composerStyles.writingPrompt}>{content.writingPrompt}</p>
        )}
        <div className={composerStyles.writingContainer}>
            <div className={styles.writingMock}>{content.writingSample}</div>
        </div>
        <MockActionRow />
    </div>
)

const DeliveryMock = ({content}: {content: DeliveryMockContent}) => (
    <div className={styles.mockPanel} aria-hidden="true">
        <p className={styles.deliveryText}>{content.acknowledgement}</p>
        <div className={styles.audioMock}>
            <div className={styles.playIcon}>
                <PlayIcon />
            </div>
            <div className={styles.progressTrack}>
                <div className={styles.progressFill} />
            </div>
        </div>
    </div>
)

// --- Main Component ---

export const SessionWalkthrough = ({sectionTitle, frames, shuffleHint, bare}: SessionWalkthroughProps) => {
    return (
        <div className={bare ? `${styles.walkthrough} ${styles.bare}` : styles.walkthrough}>
            {sectionTitle && <h2>{sectionTitle}</h2>}
            <div className={styles.frames}>
                {frames.map((frame, i) => (
                    <div key={`frame-${frame.title}`} className={styles.frame}>
                        <div className={styles.frameText}>
                            <span className={styles.frameNumber}>{i + 1}</span>
                            <h3 className={styles.frameTitle}>{frame.title}</h3>
                            <p className={styles.frameDescription}>{frame.description}</p>
                        </div>
                        {frame.content.type === 'slider' && (
                            <SliderMock content={frame.content} shuffleHint={shuffleHint} />
                        )}
                        {frame.content.type === 'toggleAndWriting' && (
                            <ToggleAndWritingMock content={frame.content} />
                        )}
                        {frame.content.type === 'delivery' && (
                            <DeliveryMock content={frame.content} />
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}
